import { apiBaseUrl } from '@/lib/api-client';

import type { TokenProvider } from './auth-identity';
import {
	DocArchivedError,
	DocConflictError,
	DocConvertedError,
	DocDeletedError,
	DocLimitError,
	DocTooLargeError,
	ProRequiredError,
	RemoteUnreachableError,
	type DocSyncMode,
	type StoredDocEnvelope,
} from './doc-store';

export type RemoteDocMeta<TMetaExtra> = {
	id: string;
	name: string;
	revision: number;
	createdAt: string;
	updatedAt: string;
	hasThumbnail: boolean;
	/** SP-52: how the server syncs this document (absent from pre-A1 servers). */
	sync?: DocSyncMode;
	/** The server froze this document (plan limit); absent from servers that
	 *  predate archiving, which reads as not archived. Optional on the wire even
	 *  where the client-side extras carry a required `archived`, hence the Omit. */
	archived?: boolean;
} & Omit<TMetaExtra, 'archived'>;

/** The remote surface SyncedDocStore consumes — implemented by
 *  RemoteDocStore over HTTP and by in-memory fakes in tests. */
export interface RemoteDocClient<TDoc extends StoredDocEnvelope, TMetaExtra> {
	list(): Promise<RemoteDocMeta<TMetaExtra>[]>;
	get(id: string): Promise<{ doc: TDoc; revision: number; sync?: DocSyncMode; archived?: boolean } | null>;
	/** `opts.archive` asks the server to store the document already archived —
	 *  how work is brought in when the account has no free active slot. */
	put(doc: TDoc, baseRevision: number | null, opts?: { archive?: boolean }): Promise<number>;
	delete(id: string): Promise<void>;
	putThumbnail(id: string, blob: Blob): Promise<void>;
	getThumbnail(id: string): Promise<Blob | null>;
}

export type RemoteDocStoreConfig<TDoc extends StoredDocEnvelope> = {
	getToken: TokenProvider;
	/** Resource base path, no trailing slash — e.g. '/api/diagrams'. */
	basePath: string;
	/** Key holding the meta array in the list response — e.g. 'diagrams'. */
	listKey: string;
	/** Wire key for a single document, in both the GET response and the PUT
	 *  request body — e.g. 'diagram'. Part of the backend contract; the
	 *  client-side return key is always `doc`. */
	docKey: string;
	parse: (raw: unknown) => TDoc;
	/** Client-side mirror of the server's whole-body cap — fail fast
	 *  without a round-trip. */
	maxPutBodyBytes: number;
	/** Error message thrown when a PUT body exceeds maxPutBodyBytes. */
	tooLargeMessage: string;
	baseUrl?: string;
	fetchFn?: typeof fetch;
};

/** HTTP client for a synced-document resource. Fetches a FRESH Clerk token
 *  per request (they expire in ~60s). Throws DocConflictError on 409 and
 *  RemoteUnreachableError on network failure / missing token / 401·403 —
 *  the two signals SyncedDocStore branches on. A PUT the server refuses on plan
 *  grounds throws DocLimitError / ProRequiredError (402) or DocArchivedError
 *  (423): the server answered, so none of them is RemoteUnreachableError. */
export class RemoteDocStore<TDoc extends StoredDocEnvelope, TMetaExtra>
implements RemoteDocClient<TDoc, TMetaExtra> {
	private readonly config: RemoteDocStoreConfig<TDoc>;
	private readonly getToken: TokenProvider;
	private readonly baseUrl: string | null;
	private readonly fetchFn: typeof fetch;

	constructor(config: RemoteDocStoreConfig<TDoc>) {
		this.config = config;
		this.getToken = config.getToken;
		this.baseUrl = config.baseUrl ?? null;
		this.fetchFn = config.fetchFn ?? ((...args) => fetch(...args));
	}

	protected async request(path: string, init: RequestInit = {}, opts: { allowForbidden?: boolean } = {}): Promise<Response> {
		let token: string | null;
		try {
			token = await this.getToken();
		} catch {
			token = null;
		}
		if (token == null) throw new RemoteUnreachableError('no auth token');
		const headers = new Headers(init.headers);
		headers.set('Authorization', `Bearer ${token}`);
		let res: Response;
		try {
			res = await this.fetchFn(`${this.baseUrl ?? apiBaseUrl()}${path}`, { ...init, headers });
		} catch {
			throw new RemoteUnreachableError('network failure');
		}
		if (res.status === 401 || (res.status === 403 && !opts.allowForbidden)) throw new RemoteUnreachableError(`auth ${res.status}`);
		// A backend incident (5xx) or rate limiting (429) must degrade to the
		// local copy like any other unreachable-remote case, not surface as a
		// hard error / lock users out.
		if (res.status >= 500 || res.status === 429) throw new RemoteUnreachableError(`server ${res.status}`);
		return res;
	}

	async list(): Promise<RemoteDocMeta<TMetaExtra>[]> {
		const res = await this.request(this.config.basePath);
		if (!res.ok) throw new Error(`GET ${this.config.basePath} failed: ${res.status}`);
		const body = (await res.json()) as Record<string, unknown>;
		return body[this.config.listKey] as RemoteDocMeta<TMetaExtra>[];
	}

	async get(id: string): Promise<{ doc: TDoc; revision: number; sync?: DocSyncMode; archived: boolean } | null> {
		const res = await this.request(`${this.config.basePath}/${id}`);
		if (res.status === 404) return null;
		if (res.status === 410) throw await goneError(res);
		if (!res.ok) throw new Error(`GET ${this.config.basePath}/${id} failed: ${res.status}`);
		const body = (await res.json()) as Record<string, unknown>;
		return {
			doc: this.config.parse(body[this.config.docKey]),
			revision: (body as { revision: number }).revision,
			sync: body.sync === 'yjs' ? 'yjs' : 'legacy',
			archived: body.archived === true,
		};
	}

	async put(doc: TDoc, baseRevision: number | null, opts: { archive?: boolean } = {}): Promise<number> {
		const body = JSON.stringify({
			[this.config.docKey]: doc,
			baseRevision,
			// Sent only when set: a plain save must not change what older servers see.
			...(opts.archive === true ? { archive: true } : {}),
		});
		// Mirror of the server's whole-body cap — fail fast without a round-trip.
		if (new TextEncoder().encode(body).length > this.config.maxPutBodyBytes) {
			throw new DocTooLargeError(this.config.tooLargeMessage);
		}
		const res = await this.request(`${this.config.basePath}/${doc.id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body,
		});
		if (res.status === 410) throw await goneError(res);
		if (res.status === 423) throw new DocArchivedError();
		if (res.status === 402) {
			const refusal = await planRefusal(res);
			if (refusal != null) throw refusal;
		}
		if (res.status === 409) {
			const body = (await res.json().catch(() => ({}))) as { currentRevision?: number | null; sync?: unknown };
			if (body.sync === 'yjs') throw new DocConvertedError(body.currentRevision ?? null);
			throw new DocConflictError(body.currentRevision ?? null);
		}
		if (!res.ok) throw new Error(`PUT ${this.config.basePath}/${doc.id} failed: ${res.status}`);
		return ((await res.json()) as { revision: number }).revision;
	}

	async delete(id: string): Promise<void> {
		const res = await this.request(`${this.config.basePath}/${id}`, { method: 'DELETE' });
		if (!res.ok && res.status !== 404) throw new Error(`DELETE ${this.config.basePath}/${id} failed: ${res.status}`);
	}

	async putThumbnail(id: string, blob: Blob): Promise<void> {
		const res = await this.request(`${this.config.basePath}/${id}/thumbnail`, {
			method: 'PUT',
			headers: { 'Content-Type': 'image/png' },
			body: blob,
		});
		if (!res.ok) throw new Error(`PUT thumbnail failed: ${res.status}`);
	}

	async getThumbnail(id: string): Promise<Blob | null> {
		const res = await this.request(`${this.config.basePath}/${id}/thumbnail`);
		if (res.status === 404) return null;
		if (!res.ok) throw new Error(`GET thumbnail failed: ${res.status}`);
		return res.blob();
	}
}

/** Maps a 402 body to its typed error, or null for any other body. The `error`
 *  code is the contract; a limit refusal without a numeric `limit` is a broken
 *  contract and stays a plain failure rather than an upgrade wall with no number. */
export async function planRefusal(res: Response): Promise<DocLimitError | ProRequiredError | null> {
	const body = (await res.json().catch(() => ({}))) as { error?: unknown; limit?: unknown };
	if (body.error === 'pro_required') return new ProRequiredError();
	if (body.error === 'diagram_limit_reached' && typeof body.limit === 'number') return new DocLimitError(body.limit);
	return null;
}

/** Builds a DocDeletedError from a 410 body. The body is advisory — the STATUS
 *  is the contract — so a malformed or missing body still yields a tombstone. */
export async function goneError(res: Response): Promise<DocDeletedError> {
	const body = (await res.json().catch(() => ({}))) as { deletedAt?: unknown };
	return new DocDeletedError(typeof body.deletedAt === 'string' ? body.deletedAt : null);
}
